import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
interface PrivacyPolicyProps {
  isOpen: boolean;
  onClose: () => void;
}
export function PrivacyPolicy({
  isOpen,
  onClose
}: PrivacyPolicyProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  return <AnimatePresence data-id="element-365" data-mp="189kjwjntsosu">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.3
    }} className="fixed inset-0 z-50 flex items-start justify-center bg-black/85 backdrop-blur-sm" onClick={onClose} data-id="element-366" data-mp="18alukn0xj9a1">
          {/* Close Button */}
          <button className="fixed top-4 right-4 md:top-6 md:right-6 text-white/70 hover:text-white transition-colors z-[60] bg-black/40 p-2 rounded-full backdrop-blur-md" onClick={e => {
        e.stopPropagation();
        onClose();
      }} data-id="element-367" data-mp="oj7doyuo63fc">
            <X size={28} data-id="element-368" data-mp="3aoexqmokqa5n" />
          </button>

          {/* Content Container */}
          <motion.div initial={{
        scale: 0.95,
        opacity: 0,
        y: 20
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0.95,
        opacity: 0,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="relative w-full max-w-3xl mx-4 my-8 md:my-12 max-h-[90vh] overflow-y-auto bg-[#0f1d35] border border-white/10 rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-369" data-mp="3fn89a9kavr36">
            <div className="p-6 md:p-10 lg:p-12" data-id="element-370" data-mp="289o3ittpwa07">
              {/* Header */}
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-2 pr-8" data-id="element-371" data-mp="237yqj8mxpm9h">
                Ochrana a nakládání s osobními údaji
              </h1>
              <p className="text-[#38bdf8] font-mono text-sm mb-6" data-id="element-372" data-mp="2c9er6ony6a0i">LKHV.eu</p>
              <div className="border-t border-white/10 mb-6" data-id="element-373" data-mp="2c7c3wce6iuzh" />
              <p className="text-white/50 text-sm italic mb-8" data-id="element-374" data-mp="1ok3wnox6xbut">
                Datum poslední aktualizace: 10. 02. 2026
              </p>

              {/* Intro */}
              <p className="text-white/70 text-sm leading-relaxed mb-8" data-id="element-375" data-mp="1y9x4ceu7p6ts">
                Tento dokument má za cíl poskytnout vám, našim zákazníkům a
                návštěvníkům webových stránek, jasné informace o tom, jak
                Letiště{' '}
                <strong className="text-white" data-id="element-376" data-mp="15r009vsl8kwi">Hořovice (AD LKHV)</strong>{' '}
                zpracovává vaše osobní údaje, v souladu s Nařízením Evropského
                parlamentu a Rady (EU) 2016/679 (GDPR).
              </p>

              {/* 1. Správce osobních údajů */}
              <h2 className="text-xl font-bold text-white mb-4" data-id="element-377" data-mp="3h9mnplvi6tyb">
                1. Správce osobních údajů
              </h2>
              <p className="text-white/70 text-sm leading-relaxed mb-4" data-id="element-378" data-mp="33eu1y5p0shja">
                Správcem, který určuje účely a prostředky zpracování vašich
                osobních údajů, je:
              </p>
              <ul className="list-disc list-inside text-white/70 text-sm space-y-1.5 mb-8 ml-2" data-id="element-379" data-mp="3w3hu8l86s76c">
                <li data-id="element-380" data-mp="3z75xwot16aw">
                  <strong className="text-white/90" data-id="element-381" data-mp="3m7epr8pv3wg8">Název společnosti:</strong>{' '}
                  Aviatický klub Tlustice, z. s.
                </li>
                <li data-id="element-382" data-mp="3r4emuu4qhov7">
                  <strong className="text-white/90" data-id="element-383" data-mp="3r82j411kcpdm">Sídlo:</strong> č.p. 258,
                  268 01 Tlustice
                </li>
                <li data-id="element-384" data-mp="2frlzrwmw1olh">
                  <strong className="text-white/90" data-id="element-385" data-mp="2fs45djvfxf62">
                    E-mail pro dotazy GDPR/Kontaktní e-mail:
                  </strong>{' '}
                  info@aviatickyklubtlustice.cz
                </li>
                <li data-id="element-386" data-mp="2q8g2zsqt8nb8">
                  <strong className="text-white/90" data-id="element-387" data-mp="2q7wji7lihqv3">Telefon:</strong> +420 606
                  730 374
                </li>
              </ul>

              {/* 2. Účely a právní základ zpracování */}
              <h2 className="text-xl font-bold text-white mb-4" data-id="element-388" data-mp="2h7871hbrnykb">
                2. Účely a právní základ zpracování
              </h2>
              <p className="text-white/70 text-sm leading-relaxed mb-4" data-id="element-389" data-mp="39xgrjftryv22">
                Vaše osobní údaje zpracováváme pouze tehdy, máme-li pro to
                zákonný důvod (právní titul):
              </p>
              <div className="overflow-x-auto -mx-2 px-2 mb-8" data-id="element-390" data-mp="300lul1d5h2pw">
                <table className="w-full text-left text-sm min-w-[580px] border border-white/10 rounded-lg overflow-hidden" data-id="element-391" data-mp="1tjqkq1b9tsoq">
                  <thead className="bg-[#0a1628]" data-id="element-392" data-mp="1okdx3eckshl3">
                    <tr data-id="element-393" data-mp="1omqf1ric2ry5">
                      <th className="p-3 text-white/60 font-medium border-b border-white/10" data-id="element-394" data-mp="1egyu8uzo9x1n">
                        Účel zpracování (Proč)
                      </th>
                      <th className="p-3 text-white/60 font-medium border-b border-white/10" data-id="element-395" data-mp="1jdp9jg3nkom0">
                        Právní základ (GDPR Článek)
                      </th>
                      <th className="p-3 text-white/60 font-medium border-b border-white/10" data-id="element-396" data-mp="1t6eia37tjodn">
                        Kategorie údajů
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5" data-id="element-397" data-mp="224q7c0t1ee3z">
                    <tr data-id="element-398" data-mp="2754gjm6dju3l">
                      <td className="p-3 text-white/70" data-id="element-399" data-mp="276pab9kv790e">
                        <strong className="text-white/90" data-id="element-400" data-mp="2c479w9czsbvq">
                          Plnění smlouvy o službách
                        </strong>{' '}
                        (např. parkování, služby pro privátní uživatele)
                      </td>
                      <td className="p-3 text-white/70" data-id="element-401" data-mp="hqzlp0jiuyj2">
                        <strong className="text-white/90" data-id="element-402" data-mp="9p8i9z81adud">
                          Plnění smlouvy
                        </strong>{' '}
                        (čl. 6 odst. 1 písm. b)
                      </td>
                      <td className="p-3 text-white/70" data-id="element-403" data-mp="jo7q5fkz65gb">
                        Jméno, příjmení, kontaktní údaje, platební údaje.
                      </td>
                    </tr>
                    <tr data-id="element-404" data-mp="so4a53wf6g51">
                      <td className="p-3 text-white/70" data-id="element-405" data-mp="snkszpelfvs2">
                        <strong className="text-white/90" data-id="element-406" data-mp="npj7yyxlxmdy">
                          Zajištění letecké a vnitřní bezpečnosti
                        </strong>{' '}
                        (CCTV, kontrola vstupu)
                      </td>
                      <td className="p-3 text-white/70" data-id="element-407" data-mp="xgdxcc65h23m">
                        <strong className="text-white/90" data-id="element-408" data-mp="1bf3t9oh8gvqe">
                          Plnění právní povinnosti
                        </strong>{' '}
                        (čl. 6 odst. 1 písm. c){' '}
                        <strong className="text-white/90" data-id="element-409" data-mp="1q1t1qftgqj8x">
                          a Oprávněný zájem
                        </strong>{' '}
                        (čl. 6 odst. 1 písm. f)
                      </td>
                      <td className="p-3 text-white/70" data-id="element-410" data-mp="1h1nv47w3fovt">
                        Vizuální záznamy z kamer, Identifikační údaje při vstupu
                        do neveřejných prostor.
                      </td>
                    </tr>
                    <tr data-id="element-411" data-mp="17ajivfxsyi4o">
                      <td className="p-3 text-white/70" data-id="element-412" data-mp="1798djbopl2bg">
                        <strong className="text-white/90" data-id="element-413" data-mp="1c333uxytkfji">
                          Plnění zákonných povinností
                        </strong>{' '}
                        (daně, účetnictví, civilní letectví)
                      </td>
                      <td className="p-3 text-white/70" data-id="element-414" data-mp="12a5k6oi3ckjb">
                        <strong className="text-white/90" data-id="element-415" data-mp="odi8tgpe5rtv">
                          Plnění právní povinnosti
                        </strong>{' '}
                        (čl. 6 odst. 1 písm. c)
                      </td>
                      <td className="p-3 text-white/70" data-id="element-416" data-mp="18t1mwlmslaqd">
                        Fakturační údaje, archivační údaje dle zákona.
                      </td>
                    </tr>
                    <tr data-id="element-417" data-mp="zru1h45dk4dp">
                      <td className="p-3 text-white/70" data-id="element-418" data-mp="zr23jmob1uk9">
                        <strong className="text-white/90" data-id="element-419" data-mp="uulfstl0pi7p">
                          Marketing a zasílání newsletterů
                        </strong>
                      </td>
                      <td className="p-3 text-white/70" data-id="element-420" data-mp="l48x76wrjz47">
                        <strong className="text-white/90" data-id="element-421" data-mp="l2flveb7gqpv">Souhlas</strong> (čl.
                        6 odst. 1 písm. a){' '}
                        <strong className="text-white/90" data-id="element-422" data-mp="g2jhvf2iczab">
                          nebo Oprávněný zájem
                        </strong>{' '}
                        (u stávajících zákazníků)
                      </td>
                      <td className="p-3 text-white/70" data-id="element-423" data-mp="25eejmu6ieir">
                        E-mail, případně preference.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* 3. Specifické zpracování */}
              <h2 className="text-xl font-bold text-white mb-4" data-id="element-424" data-mp="2ite4zna45fl9">
                3. Specifické zpracování osobních údajů
              </h2>
              <div className="space-y-4 mb-8" data-id="element-425" data-mp="2rsj1ko246yus">
                <div data-id="element-426" data-mp="2rq55qczm1im6">
                  <h3 className="text-white/90 font-semibold text-sm mb-1" data-id="element-427" data-mp="1v1ytp50cznxv">
                    a) Kamerový systém (CCTV)
                  </h3>
                  <p className="text-white/70 text-sm leading-relaxed" data-id="element-428" data-mp="201v35jrts7kt">
                    Na letišti je provozován kamerový systém za účelem ochrany
                    majetku a zajištění bezpečnosti letového provozu. Záznamy
                    jsou uchovávány po dobu nezbytně nutnou (obvykle 72 hodin) a
                    přístup k nim mají pouze oprávněné osoby.
                  </p>
                </div>
                <div data-id="element-429" data-mp="1gb9iw3qt8auj">
                  <h3 className="text-white/90 font-semibold text-sm mb-1" data-id="element-430" data-mp="1gaq1co52noh1">
                    b) Cookies a webová analytika
                  </h3>
                  <p className="text-white/70 text-sm leading-relaxed" data-id="element-431" data-mp="2ld8srlv2j250">
                    Webové stránky lkhv.eu mohou používat technické cookies
                    nezbytné pro fungování webu. Analytické cookies jsou použity
                    pouze s vaším souhlasem.
                  </p>
                </div>
                <div data-id="element-432" data-mp="1xqadusmlk297">
                  <h3 className="text-white/90 font-semibold text-sm mb-1" data-id="element-433" data-mp="1xmmlvxwsvnwl">
                    c) Kontaktní formulář
                  </h3>
                  <p className="text-white/70 text-sm leading-relaxed" data-id="element-434" data-mp="26ovwens2jh9z">
                    Pokud nás kontaktujete prostřednictvím e-mailu nebo
                    telefonu, zpracováváme vaše kontaktní údaje za účelem
                    vyřízení vašeho dotazu.
                  </p>
                </div>
              </div>

              {/* 4. Doba zpracování a uložení */}
              <h2 className="text-xl font-bold text-white mb-4" data-id="element-435" data-mp="1bgaabkp10sdv">
                4. Doba zpracování a uložení
              </h2>
              <p className="text-white/70 text-sm leading-relaxed mb-4" data-id="element-436" data-mp="16gxrrc56u7h3">
                Osobní údaje uchováváme pouze po dobu nezbytně nutnou k naplnění
                účelu zpracování:
              </p>
              <ul className="list-disc list-inside text-white/70 text-sm space-y-1.5 mb-8 ml-2" data-id="element-437" data-mp="xfhgl26ju1bt">
                <li data-id="element-438" data-mp="sihknk3eyeyf">
                  Smluvní údaje: po dobu trvání smlouvy + zákonná archivační
                  lhůta
                </li>
                <li data-id="element-439" data-mp="nomt5uhkgvos">Účetní doklady: 5–10 let dle zákona o účetnictví</li>
                <li data-id="element-440" data-mp="is6510adahbp">CCTV záznamy: obvykle 72 hodin</li>
                <li data-id="element-441" data-mp="dstk4l323kbr">Marketingové souhlasy: do odvolání souhlasu</li>
                <li data-id="element-442" data-mp="dqgtljj8g1n3">
                  Údaje z kontaktních formulářů: po dobu vyřízení dotazu, max. 1
                  rok
                </li>
              </ul>

              {/* 5. Příjemci osobních údajů */}
              <h2 className="text-xl font-bold text-white mb-4" data-id="element-443" data-mp="4u7uy732x720">
                5. Příjemci osobních údajů
              </h2>
              <p className="text-white/70 text-sm leading-relaxed mb-4" data-id="element-444" data-mp="3vbqpyowzvkxe">
                Vaše osobní údaje mohou být předány následujícím kategoriím
                příjemců:
              </p>
              <ul className="list-disc list-inside text-white/70 text-sm space-y-1.5 mb-8 ml-2" data-id="element-445" data-mp="3ma0wzengsdxi">
                <li data-id="element-446" data-mp="du7ggzt4v2gg">
                  Orgány státní správy a dohledu (ÚCL, Policie ČR) v zákonem
                  stanovených případech
                </li>
                <li data-id="element-447" data-mp="ivwtum3tvs7p">
                  Poskytovatelé IT služeb a hostingu (zpracovatelé na základě
                  smlouvy)
                </li>
                <li data-id="element-448" data-mp="93hezxntvjr1">Účetní a daňoví poradci</li>
              </ul>
              <p className="text-white/70 text-sm leading-relaxed mb-8" data-id="element-449" data-mp="91o589hx4jet">
                Osobní údaje nejsou předávány do třetích zemí mimo EU/EHP, pokud
                to není nezbytné a zajištěno odpovídajícími zárukami.
              </p>

              {/* 6. Zabezpečení */}
              <h2 className="text-xl font-bold text-white mb-4" data-id="element-450" data-mp="2m8w6a43pq48q">
                6. Zabezpečení osobních údajů
              </h2>
              <p className="text-white/70 text-sm leading-relaxed mb-8" data-id="element-451" data-mp="2v30z2t2eppqw">
                Přijali jsme vhodná technická a organizační opatření k ochraně
                vašich osobních údajů před neoprávněným přístupem, ztrátou,
                zničením nebo zneužitím. Přístup k údajům mají pouze oprávněné
                osoby vázané mlčenlivostí.
              </p>

              {/* 7. Vaše práva */}
              <h2 className="text-xl font-bold text-white mb-4" data-id="element-452" data-mp="2ccazbd84uj29">
                7. Vaše práva
              </h2>
              <p className="text-white/70 text-sm leading-relaxed mb-4" data-id="element-453" data-mp="27cy9cjlysvvg">
                V souvislosti se zpracováním osobních údajů máte následující
                práva:
              </p>
              <ul className="list-disc list-inside text-white/70 text-sm space-y-1.5 mb-6 ml-2" data-id="element-454" data-mp="3it4sk7lvs946">
                <li data-id="element-455" data-mp="hxnnjxzjm1dq">
                  <strong className="text-white/90" data-id="element-456" data-mp="hygw94i2b0yi">Právo na přístup</strong> –
                  máte právo vědět, jaké údaje o vás zpracováváme
                </li>
                <li data-id="element-457" data-mp="rvlb0j9kvu5a">
                  <strong className="text-white/90" data-id="element-458" data-mp="rvbsfgqv4p9m">Právo na opravu</strong> –
                  můžete požádat o opravu nepřesných údajů
                </li>
                <li data-id="element-459" data-mp="wslh2u6fgwwe">
                  <strong className="text-white/90" data-id="element-460" data-mp="10wjdgx4dbtlr">Právo na výmaz</strong> –
                  můžete požádat o smazání údajů, pokud pominul účel zpracování
                </li>
                <li data-id="element-461" data-mp="15t01lrbki7yu">
                  <strong className="text-white/90" data-id="element-462" data-mp="1amcpu0cc4cr2">
                    Právo na omezení zpracování
                  </strong>{' '}
                  – můžete požádat o dočasné omezení zpracování
                </li>
                <li data-id="element-463" data-mp="1kl1wewys6as0">
                  <strong className="text-white/90" data-id="element-464" data-mp="1klk0gfroptah">
                    Právo na přenositelnost
                  </strong>{' '}
                  – máte právo získat své údaje ve strukturovaném formátu
                </li>
                <li data-id="element-465" data-mp="3957oaf4jdghi">
                  <strong className="text-white/90" data-id="element-466" data-mp="352b3qivfraax">
                    Právo vznést námitku
                  </strong>{' '}
                  – proti zpracování na základě oprávněného zájmu
                </li>
                <li data-id="element-467" data-mp="2v4dhu211tplh">
                  <strong className="text-white/90" data-id="element-468" data-mp="2v4x1bn6ckm1m">
                    Právo odvolat souhlas
                  </strong>{' '}
                  – kdykoli, bez vlivu na zákonnost předchozího zpracování
                </li>
              </ul>
              <p className="text-white/70 text-sm leading-relaxed mb-4" data-id="element-469" data-mp="3ntkmzjv0ddfp">
                Pro uplatnění svých práv nás kontaktujte na:{' '}
                <strong className="text-[#38bdf8]" data-id="element-470" data-mp="3e39cfegtk5zu">
                  info@aviatickyklubtlustice.cz
                </strong>
              </p>
              <p className="text-white/70 text-sm leading-relaxed mb-8" data-id="element-471" data-mp="fek85rks419x">
                Pokud se domníváte, že zpracování vašich osobních údajů je v
                rozporu s právními předpisy, máte právo podat stížnost u
                dozorového úřadu –{' '}
                <strong className="text-white/90" data-id="element-472" data-mp="28w800amkqbva">
                  Úřadu pro ochranu osobních údajů (ÚOOÚ)
                </strong>
                , www.uoou.cz.
              </p>

              <div className="border-t border-white/10 pt-6" data-id="element-473" data-mp="2034s0hup6r81">
                <p className="text-white/40 text-xs text-center" data-id="element-474" data-mp="201jymvk4debr">
                  © 2026 Aviatický klub Tlustice, z.s. Všechna práva vyhrazena.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}